import React from 'react';
import PillarLayout from './PillarLayout';
import { SOCIAL_IMAGE } from '../../seo';

const P = ({ children }) => <p className="mb-4">{children}</p>;
const UL = ({ children }) => <ul className="mb-4 pl-5 list-disc space-y-1">{children}</ul>;
const Q = ({ children }) => (
  <blockquote className="my-5 pl-4" style={{ borderLeft: '3px solid var(--nb-accent)', fontFamily: 'var(--script)', color: 'var(--ink)', fontSize: 'var(--t-md)', lineHeight: 1.3 }}>
    {children}
  </blockquote>
);

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Design evidence should be a byproduct, not a deliverable',
  description: "Most hardware engineering documentation fails because it's treated as a separate deliverable that competes with the work for time. The fix is to make the act of doing the engineering produce the record — so documentation falls out as a byproduct.",
  author: { '@type': 'Organization', name: 'Factory OS' },
  publisher: { '@type': 'Organization', name: 'Factory OS', url: 'https://factory-os.com', logo: { '@type': 'ImageObject', url: 'https://factory-os.com/og-image.png' } },
  datePublished: '2026-07-01',
  dateModified: '2026-07-24',
  image: SOCIAL_IMAGE,
  mainEntityOfPage: 'https://factory-os.com/resources/design-evidence-as-byproduct',
  about: ['Engineering documentation', 'Design rationale', 'Engineering evidence', 'Hardware engineering process'],
};

const DesignEvidenceAsByproduct = () => (
  <PillarLayout
    meta={{
      title: 'Design evidence should be a byproduct, not a deliverable | Factory OS',
      description: "Hardware engineering documentation fails because it's a separate deliverable that competes with the work. Make the act of doing the engineering produce the record. Here's how that works in practice.",
      path: '/resources/design-evidence-as-byproduct',
    }}
    hero={{
      eyebrow: 'the engineering operating system · the differentiator',
      h1: 'Design evidence should be a byproduct, not a deliverable.',
      thesis: "Documentation that competes with the work for time loses every time.",
    }}
    articleSchema={articleSchema}
    related={[
      { href: '/resources/what-is-an-engineering-operating-system', title: 'What is an Engineering Operating System?' },
      { href: '/resources/engineering-operating-system-vs-plm', title: 'Engineering Operating System vs. PLM' },
      { href: '/resources/iadt-verification-for-hardware-teams', title: 'IADT verification, applied to hardware teams' },
    ]}
    sections={[
      {
        heading: 'The documentation paradox',
        body: (
          <>
            <P>
              Every hardware engineering organization knows that good documentation is the difference between a part that gets re-derived from scratch on the next revision and a part that compounds. Every hardware engineering organization also has documentation that's months out of date, populated in a panic before a program review, or missing entirely for the decisions that matter most.
            </P>
            <P>
              The standard explanation is "engineers don't like writing documentation." The standard fix is to staff a technical-writing role or pay for a heavier PLM. Both miss the actual diagnosis.
            </P>
            <Q>
              Documentation doesn't fail because engineers don't like writing it. It fails because it's set up as a separate task that competes with the engineering for time. Engineering wins every time.
            </Q>
            <P>
              You can't out-discipline a structural problem. As long as documentation is a parallel deliverable — a second track of work the engineer has to remember to do on top of designing the part — it will be dropped under pressure, and pressure is the default state of a hardware program.
            </P>
          </>
        ),
      },
      {
        heading: 'The byproduct principle',
        body: (
          <>
            <P>
              The alternative is to design the tool so that doing the work <i>produces</i> the documentation as a side effect — not as a parallel step. The engineer doesn't write up the trade study after; they pick a concept and the rejected alternatives are already on the node, with cost and lead-time deltas attached, because that's how concept selection works inside the tool.
            </P>
            <P>
              This is not a documentation tool with good UX. It's a different shape of product. The interaction loop is:
            </P>
            <UL>
              <li>The engineer does the engineering inside the system.</li>
              <li>The system requires evidence to advance to the next phase.</li>
              <li>The evidence the system asks for is the documentation the program would ask for next year.</li>
              <li>So the documentation is a side effect of advancing the work. No second track.</li>
            </UL>
            <P>
              The DevOps analogy is exact. Software engineers don't write "what changed in this commit?" as a documentation task — git logs it as a byproduct of the act of committing. Software engineers don't write "what was the result of this build?" — CI logs it. The act of doing the work produces the record. That's why software has a record and hardware mostly doesn't.
            </P>
          </>
        ),
      },
      {
        heading: 'Four concrete examples',
        body: (
          <>
            <P>
              Here's the byproduct principle, applied to four artifacts that hardware teams traditionally do as separate deliverables:
            </P>
            <P>
              <b>1. Trade studies.</b> The traditional version is a separate document the engineer writes after concept selection, listing alternatives considered and the reason for choosing one. The byproduct version: the system asks the engineer to log each candidate concept with material, process, and geometry, then to assign cost and lead-time deltas. Concept selection is the act of picking one and writing a one-sentence rationale. The trade study exists because concept selection happened, not because someone wrote it up.
            </P>
            <P>
              <b>2. Verification matrix.</b> The traditional version is a spreadsheet assembled before a program review, mapping requirements to test results. The byproduct version: every requirement carries an IADT verification method tagged at write time. Every test result attaches to the requirement it proves, with measured value vs. target and tolerance. The verification matrix is the join of the requirement table and the test-result table. It exists at all times. Nobody assembles it; it's a view.
            </P>
            <P>
              <b>3. Design history file (DHF).</b> The traditional version, in regulated industries, is a binder assembled at the end of a program containing the full record of design inputs, outputs, reviews, verifications, and changes. The byproduct version: every revision is a sequence of node states with the decisions between them. The DHF is a slice of that record, generated, not assembled.
            </P>
            <P>
              <b>4. Lessons learned.</b> The traditional version is a wiki page or PowerPoint deck someone is supposed to write after a project. Almost never written. The byproduct version: every failure that opens a new revision is cited on the new revision, with the failure data attached. The next person to start a similar revision sees the failures of the previous one before they design anything. Lessons learned aren't a document — they're a property of the carry-forward mechanism.
            </P>
          </>
        ),
      },
      {
        heading: 'What this changes in practice',
        body: (
          <>
            <P>
              When evidence is a byproduct, three things stop being problems and one new thing starts being possible.
            </P>
            <UL>
              <li><b>Onboarding stops being a rite of passage.</b> The new engineer reads the decisions on the node. The "why 4130 over Ti" question has an answer on the part, not in the head of an engineer who left.</li>
              <li><b>Audit prep stops being a fire drill.</b> The traceability matrix exists at all times. The program review reads it; nobody assembles it overnight.</li>
              <li><b>Revisions stop restarting from a blank page.</b> Carry-forward gives the next revision the failure data and the proven inputs of the last one before any work happens.</li>
              <li><b>And — this is the new thing — model accuracy starts compounding.</b> When predicted-vs-actual correlation is tracked across revisions as a byproduct of running the loop, the next engineer to write an FEA prediction knows how much the previous predictions over- or under-shot, and can calibrate. Engineering judgment is no longer tribal.</li>
            </UL>
          </>
        ),
      },
      {
        heading: 'The failure mode of the alternative',
        body: (
          <>
            <P>
              The standard response to "we have a documentation problem" is to buy a heavier tool — a PLM with mandatory metadata fields, an ALM with workflow enforcement, an ELN with audit trails. These tools work in the sense that they exist; they fail in the sense that engineers run a parallel track in spreadsheets and Slack because the heavy tool slows down the design work.
            </P>
            <Q>
              You can't buy your way out of a problem caused by tools sitting next to the work.
            </Q>
            <P>
              The fix isn't to make the documentation tool heavier. The fix is to put the engineer inside the work, and let the work produce the documentation. Once you've done that, the heavier documentation tool stops being necessary — the record is already there.
            </P>
          </>
        ),
      },
      {
        heading: 'The principle, restated',
        body: (
          <>
            <P>
              Three sentences:
            </P>
            <Q>
              Documentation that competes with the work loses. Make doing the work produce the record. Then the record is a property of the system, not a discipline of the engineer.
            </Q>
            <P>
              That's the principle. The rest is how the tool is built.
            </P>
          </>
        ),
      },
    ]}
  />
);

export default DesignEvidenceAsByproduct;
